import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import BioEditor from "@/components/BioEditor";
import { Info, AlertTriangle, X, ChevronDown, ChevronUp, LogIn } from "lucide-react";

const MODUL_J_ACK_KEY = "modul-j-transparenz-ack-v1";

export default function OnboardingPage() {
  const { t } = useTranslation();
  const { refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [mode, setMode] = useState("USER");
  const [sex, setSex] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // iter88.23 · Modul-J Transparenz (Owner-Vorgabe 2026-07-18)
  const [showModulJModal, setShowModulJModal] = useState(false);
  const [modulJExpanded, setModulJExpanded] = useState(true);

  useEffect(() => {
    try {
      if (!localStorage.getItem(MODUL_J_ACK_KEY)) {
        setShowModulJModal(true);
      }
    } catch { /* noop */ }
  }, []);

  const dismissModulJModal = () => {
    try { localStorage.setItem(MODUL_J_ACK_KEY, new Date().toISOString()); } catch { /* noop */ }
    setShowModulJModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!sex) {
      setError(t("onboarding.sex_required"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/profile/onboarding", {
        display_name: displayName,
        // handle wird Backend-seitig auto-generiert (Sub-Batch UX-1)
        initial_mode: mode,
        bio,
        sex,
      });
      await refreshUser();
      navigate("/");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="onboarding-page">
      <div className="h-14 bg-[#0A0A0A] flex items-center justify-between px-4">
        <button data-testid="onboarding-back-btn" onClick={() => navigate("/")} className="text-white/70 hover:text-white transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
        <div className="flex items-baseline">
          <span className="font-black text-white text-xl">still</span>
          <span className="font-normal text-[#C0C0C0] text-xl">touched</span>
        </div>
        <div className="w-5" />
      </div>

      <div className="flex-1 px-6 py-8 max-w-md mx-auto w-full">
        <h1 className="text-2xl font-bold text-[#0A0A0A] tracking-tight">{t("onboarding.create_profile")}</h1>
        <p className="text-sm text-[#999] mt-1">{t("onboarding.create_subtitle")}</p>

        {/* iter88.24 · Anmelde-Link (Owner-Bug: User hatte keinen Weg zum Login) */}
        <div className="mt-3 flex items-center gap-2 text-[13px]">
          <span className="text-[#666]">{t("onboarding.already_account")}</span>
          <button data-testid="onboarding-login-link" type="button"
            onClick={async () => {
              try { await logout(); } catch { /* noop */ }
              navigate("/login");
            }}
            className="inline-flex items-center gap-1 font-medium text-[#0A0A0A] underline underline-offset-2 hover:text-[#333]">
            <LogIn size={14} /> {t("onboarding.sign_in")}
          </button>
        </div>

        {error && (
          <InfoCard variant="danger" data-testid="onboarding-error" size="sm">{error}</InfoCard>
        )}

        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-5">
          <div>
            <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("onboarding.display_name_required")}</label>
            <input
              data-testid="onboarding-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-1 w-full h-12 px-4 border border-[#E5E5E5] bg-white text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
              placeholder={t("onboarding.display_name_placeholder")}
              required
              minLength={2}
              maxLength={50}
            />
            <div className="mt-2 flex gap-2 items-start p-2 bg-[#FAFAFA] border border-[#E5E5E5]">
              <Info size={14} className="text-[#999] mt-0.5 shrink-0" />
              <p className="text-[10px] text-[#999] leading-relaxed">
                {t("onboarding.name_unique_hint")}
              </p>
            </div>

            {/* iter88.23 · Modul-J Transparenzhinweis (Owner-Vorgabe 2026-07-18) */}
            <div data-testid="modul-j-info-box" className="mt-2 border border-amber-200 bg-amber-50">
              <button type="button" onClick={() => setModulJExpanded((v) => !v)}
                className="w-full flex items-center gap-2 p-2 text-left">
                <AlertTriangle size={14} className="text-amber-600 shrink-0" />
                <span className="flex-1 text-[11px] font-semibold text-amber-900">{t("onboarding.modul_j_title")}</span>
                {modulJExpanded ? <ChevronUp size={14} className="text-amber-700" /> : <ChevronDown size={14} className="text-amber-700" />}
              </button>
              {modulJExpanded && (
                <div className="px-3 pb-3 pt-0 space-y-2">
                  <p data-testid="modul-j-info-text" className="text-[10.5px] text-amber-900 leading-relaxed">
                    {t("onboarding.modul_j_text")}
                  </p>
                  <p className="text-[10.5px] text-amber-900/90 leading-relaxed">
                    {t("onboarding.username_editable_hint")}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("onboarding.sex_required_label")} <span className="text-[#999] normal-case font-normal">{t("onboarding.sex_immutable_hint")}</span></label>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                data-testid="onboarding-sex-female"
                onClick={() => setSex("FEMALE")}
                className={`flex-1 h-12 text-sm font-medium border transition-colors ${
                  sex === "FEMALE"
                    ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                    : "bg-white text-[#666] border-[#E5E5E5] hover:border-[#999]"
                }`}
              >
                {t("onboarding.female")}
              </button>
              <button
                type="button"
                data-testid="onboarding-sex-male"
                onClick={() => setSex("MALE")}
                className={`flex-1 h-12 text-sm font-medium border transition-colors ${
                  sex === "MALE"
                    ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                    : "bg-white text-[#666] border-[#E5E5E5] hover:border-[#999]"
                }`}
              >
                {t("onboarding.male")}
              </button>
            </div>
            <div className="mt-2 flex gap-2 items-start p-2 bg-[#FAFAFA] border border-[#E5E5E5]">
              <Info size={14} className="text-[#999] mt-0.5 shrink-0" />
              <p className="text-[10px] text-[#999] leading-relaxed">
                {t("onboarding.sex_reason_hint")}
              </p>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("onboarding.bio_label")}</label>
            <div className="mt-1">
              <BioEditor value={bio} onChange={setBio} maxLength={500} id="onboarding-bio-editor" placeholder={t("onboarding.bio_placeholder_full")} testId="onboarding-bio" />
            </div>
            <p className="text-[10px] text-amber-800 mt-1">
              {t("onboarding.bio_visible_hint")}
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("onboarding.start_mode_required")}</label>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                data-testid="onboarding-mode-user"
                onClick={() => setMode("USER")}
                className={`flex-1 h-12 text-sm font-medium border transition-colors ${
                  mode === "USER"
                    ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                    : "bg-white text-[#666] border-[#E5E5E5] hover:border-[#999]"
                }`}
              >
                User
              </button>
              <button
                type="button"
                data-testid="onboarding-mode-creator"
                onClick={() => setMode("CREATOR")}
                className={`flex-1 h-12 text-sm font-medium border transition-colors ${
                  mode === "CREATOR"
                    ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                    : "bg-white text-[#666] border-[#E5E5E5] hover:border-[#999]"
                }`}
              >
                Creator
              </button>
            </div>
          </div>

          <button
            data-testid="onboarding-submit"
            type="submit"
            disabled={loading}
            className="rounded-full h-12 bg-[#0A0A0A] text-white text-sm font-medium transition-colors hover:bg-[#222] active:scale-[0.98] disabled:opacity-50 mt-2"
          >
            {loading ? t("onboarding.saving") : t("onboarding.create_profile")}
          </button>
        </form>
      </div>

      {/* iter88.23 · Modul-J Transparenz-Modal (einmalig, gespeichert in localStorage) */}
      {showModulJModal && (
        <div data-testid="modul-j-modal" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
             onClick={dismissModulJModal}>
          <div className="bg-white max-w-md w-full p-6 border border-[#E5E5E5]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <AlertTriangle size={20} className="text-amber-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-[#0A0A0A]">{t("onboarding.modul_j_modal_title")}</h2>
                <p className="text-xs text-[#666]">{t("onboarding.modul_j_modal_body")}</p>
              </div>
              <button data-testid="modul-j-modal-close" onClick={dismissModulJModal} className="text-[#999] hover:text-[#0A0A0A]">
                <X size={20} />
              </button>
            </div>
            <p data-testid="modul-j-modal-text" className="text-sm text-[#333] leading-relaxed mb-4">
              {t("onboarding.modul_j_text")}
            </p>
            <div className="text-[10px] text-[#666] leading-relaxed mb-4 space-y-1">
              <p>{t("onboarding.modul_j_bullet_scope")}</p>
              <p>{t("onboarding.modul_j_bullet_content")}</p>
              <p>{t("onboarding.modul_j_bullet_reflect")}</p>
            </div>
            <button data-testid="modul-j-modal-ack" onClick={dismissModulJModal}
              className="w-full rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#222]">
              {t("onboarding.modul_j_understood")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
